import { WalletAdapterNetwork } from "@solana/wallet-adapter-base";
import {
  ConnectionProvider,
  WalletProvider,
  useWallet,
} from "@solana/wallet-adapter-react";
import { WalletModalProvider } from "@solana/wallet-adapter-react-ui";
import {
  PhantomWalletAdapter,
} from "@solana/wallet-adapter-wallets";
import { clusterApiUrl } from "@solana/web3.js";
import {
  requestWalletConnection,
  reportConnectionSuccess,
  reportConnectionFailure,
  reportWalletDisconnect,
  getConnectionWaitTime,
} from "app/utils/connection-coordinator";
import React, {
  FC,
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from "react";

// Default styles for wallet modal - import conditionally for SSR compatibility
if (typeof document !== "undefined") {
  require("@solana/wallet-adapter-react-ui/styles.css");
}

interface SolanaWalletContextType {
  isConnected: boolean;
  publicKey: string | null;
  associatedWithEVM: boolean;
  setAssociatedWithEVM: (associated: boolean) => void;
}

const SolanaWalletContext = createContext<SolanaWalletContextType | undefined>(
  undefined
);

interface SolanaWalletProviderProps {
  children: ReactNode;
  network?: WalletAdapterNetwork;
}

// Hook to handle safe auto-reconnection after hydration
const useSolanaAutoReconnect = () => {
  const { connect, wallets, select, connected, connecting, disconnect: _disconnect } =
    useWallet();
  const [hasAttemptedReconnect, setHasAttemptedReconnect] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isAutoReconnecting, setIsAutoReconnecting] = useState(false);
  const [userInitiatedConnection, setUserInitiatedConnection] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [lastReconnectAttempt, setLastReconnectAttempt] = useState(0);

  const MAX_RECONNECT_ATTEMPTS = 2;
  const RECONNECT_COOLDOWN = 30000; // 30 seconds between attempts

  // Wait for hydration to complete
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsHydrated(true);
    }, 1500); // Increased delay to ensure everything is ready

    return () => clearTimeout(timer);
  }, []);

  // Listen for user-initiated connection attempts
  useEffect(() => {
    const handleUserConnection = () => {
      console.log("SolanaWalletContext: User-initiated connection detected");
      setUserInitiatedConnection(true);
      setHasAttemptedReconnect(true); // Prevent auto-reconnect when user manually connects

      // Reset after 15 seconds
      setTimeout(() => {
        setUserInitiatedConnection(false);
      }, 15000);
    };

    // Listen for wallet connection events
    window.addEventListener("wallet-connect-initiated", handleUserConnection);

    // Also listen for any MetaMask connection attempts to avoid conflicts
    const handleEvmConnection = () => {
      console.log(
        "SolanaWalletContext: EVM wallet connection detected, delaying Solana auto-reconnect"
      );
      setUserInitiatedConnection(true);
      setTimeout(() => setUserInitiatedConnection(false), 20000);
    };

    window.addEventListener("metamask-connect-initiated", handleEvmConnection);

    return () => {
      window.removeEventListener(
        "wallet-connect-initiated",
        handleUserConnection
      );
      window.removeEventListener(
        "metamask-connect-initiated",
        handleEvmConnection
      );
    };
  }, []);

  const attemptAutoReconnect = useCallback(async () => {
    const now = Date.now();

    if (
      !isHydrated ||
      hasAttemptedReconnect ||
      connected ||
      connecting ||
      isAutoReconnecting ||
      userInitiatedConnection ||
      reconnectAttempts >= MAX_RECONNECT_ATTEMPTS ||
      now - lastReconnectAttempt < RECONNECT_COOLDOWN
    ) {
      return;
    }

    // Check if we should wait for other wallet connections
    const waitTime = getConnectionWaitTime("solana");
    if (waitTime > 0) {
      console.log(
        `SolanaWalletContext: Waiting ${waitTime}ms for other wallet operations to complete`
      );
      setTimeout(() => void attemptAutoReconnect(), waitTime);
      return;
    }

    // Request permission to connect through coordinator
    if (!requestWalletConnection("solana")) {
      console.log("SolanaWalletContext: Auto-reconnect denied by coordinator");
      return;
    }

    setHasAttemptedReconnect(true);
    setIsAutoReconnecting(true);
    setReconnectAttempts((prev) => prev + 1);
    setLastReconnectAttempt(now);

    try {
      // Check if there's a previously connected wallet in localStorage
      const lastWalletNameRaw = localStorage.getItem("solana-wallet-adapter");
      if (!lastWalletNameRaw) {
        setIsAutoReconnecting(false);
        return;
      }

      // Parse the JSON value (localStorage stores it as JSON string)
      let lastWalletName: string;
      try {
        lastWalletName = JSON.parse(lastWalletNameRaw);
      } catch (e) {
        // If it's not JSON, use it as-is
        lastWalletName = lastWalletNameRaw;
      }

      // Find the wallet adapter
      const walletAdapter = wallets.find(
        (wallet) => wallet.adapter.name === lastWalletName
      );

      if (!walletAdapter) {
        setIsAutoReconnecting(false);
        return;
      }

      // Check if wallet is ready (allow both 'Installed' and 'Loadable')
      if (
        !["Installed", "Loadable"].includes(walletAdapter.adapter.readyState)
      ) {
        console.log(
          `Solana wallet ${lastWalletName} not ready for auto-reconnect:`,
          walletAdapter.adapter.readyState
        );
        setIsAutoReconnecting(false);
        return;
      }

      console.log(
        `Attempting auto-reconnect to Solana wallet: ${lastWalletName} (attempt ${
          reconnectAttempts + 1
        }/${MAX_RECONNECT_ATTEMPTS})`
      );

      // Exponential backoff: longer delays for subsequent attempts
      const baseDelay = 3000;
      const exponentialDelay = baseDelay * Math.pow(2, reconnectAttempts);

      setTimeout(async () => {
        try {
          // Final check before reconnecting
          if (!connected && !connecting && !userInitiatedConnection) {
            await select(walletAdapter.adapter.name);

            // Add another delay before connecting with exponential backoff
            setTimeout(async () => {
              try {
                if (!connected && !connecting && !userInitiatedConnection) {
                  await connect();
                  console.log(
                    `Successfully auto-reconnected to Solana wallet: ${lastWalletName}`
                  );
                  // Reset attempts on successful connection
                  setReconnectAttempts(0);
                  setLastReconnectAttempt(0);
                  reportConnectionSuccess("solana");
                }
              } catch (error) {
                console.warn(
                  `Failed to auto-connect to Solana wallet: ${lastWalletName}`,
                  error
                );
                reportConnectionFailure("solana");

                // If max attempts reached, stop trying
                if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS - 1) {
                  console.log(
                    `Max auto-reconnect attempts reached for ${lastWalletName}`
                  );
                }
              }
            }, 1000);
          }
        } catch (error) {
          console.warn(
            `Failed to select Solana wallet for auto-reconnect: ${lastWalletName}`,
            error
          );
          reportConnectionFailure("solana");
        } finally {
          setIsAutoReconnecting(false);
        }
      }, exponentialDelay);
    } catch (error) {
      console.warn("Error during Solana auto-reconnect attempt:", error);
      reportConnectionFailure("solana");
      setIsAutoReconnecting(false);
    }
  }, [
    isHydrated,
    hasAttemptedReconnect,
    connected,
    connecting,
    isAutoReconnecting,
    userInitiatedConnection,
    reconnectAttempts,
    lastReconnectAttempt,
    wallets,
    select,
    connect,
    MAX_RECONNECT_ATTEMPTS,
    RECONNECT_COOLDOWN,
  ]);

  // Attempt auto-reconnect when conditions are met, with additional safety checks
  useEffect(() => {
    if (
      isHydrated &&
      !hasAttemptedReconnect &&
      !connected &&
      !connecting &&
      !isAutoReconnecting &&
      !userInitiatedConnection &&
      wallets.length > 0 &&
      reconnectAttempts < MAX_RECONNECT_ATTEMPTS
    ) {
      // Add a longer delay to ensure any manual connection attempts have priority
      const timer = setTimeout(() => {
        if (!connected && !connecting && !userInitiatedConnection) {
          void attemptAutoReconnect(); // Handle the promise to avoid warning
        }
      }, 4000); // Further increased delay to give manual connections priority

      return () => clearTimeout(timer);
    }
  }, [
    isHydrated,
    hasAttemptedReconnect,
    connected,
    connecting,
    isAutoReconnecting,
    userInitiatedConnection,
    wallets.length,
    reconnectAttempts,
    attemptAutoReconnect,
    MAX_RECONNECT_ATTEMPTS,
  ]);

  // Reset auto-reconnection state when user manually connects and report status
  useEffect(() => {
    if (connected) {
      setIsAutoReconnecting(false);
      setReconnectAttempts(0); // Reset attempts when successfully connected
      setLastReconnectAttempt(0);
      reportConnectionSuccess("solana");
    } else {
      // Report disconnection when wallet disconnects
      reportWalletDisconnect("solana");
    }
  }, [connected]);

  // Reset hasAttemptedReconnect periodically to allow retry after failures
  useEffect(() => {
    if (
      hasAttemptedReconnect &&
      !connected &&
      reconnectAttempts < MAX_RECONNECT_ATTEMPTS
    ) {
      const timer = setTimeout(() => {
        setHasAttemptedReconnect(false);
      }, RECONNECT_COOLDOWN);

      return () => clearTimeout(timer);
    }
  }, [
    hasAttemptedReconnect,
    connected,
    reconnectAttempts,
    MAX_RECONNECT_ATTEMPTS,
    RECONNECT_COOLDOWN,
  ]);

  return { hasAttemptedReconnect, isHydrated };
};

// Internal component to access wallet state and provide context
const SolanaWalletContextProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { publicKey, connected, wallet } = useWallet();
  const [associatedWithEVM, setAssociatedWithEVM] = useState(false);

  // Enable auto-reconnection
  useSolanaAutoReconnect();

  // Debug wallet context state
  React.useEffect(() => {
    console.log("SolanaWalletContext state updated:", {
      connected,
      publicKey: publicKey?.toString(),
      walletName: wallet?.adapter.name,
      associatedWithEVM,
    });
  }, [connected, publicKey, wallet, associatedWithEVM]);

  return (
    <SolanaWalletContext.Provider
      value={{
        isConnected: connected,
        publicKey: publicKey?.toString() || null,
        associatedWithEVM,
        setAssociatedWithEVM,
      }}
    >
      {children}
    </SolanaWalletContext.Provider>
  );
};

export const SolanaWalletProvider: FC<SolanaWalletProviderProps> = ({
  children,
  network = WalletAdapterNetwork.Mainnet,
}) => {
  // The network can be set to 'devnet', 'testnet', or 'mainnet-beta'.
  const endpoint = useMemo(() => clusterApiUrl(network), [network]);

  const wallets = useMemo(
    () => [
      // Only include PhantomWalletAdapter if it's not already registered as a Standard Wallet
      // This prevents the "Phantom was registered as a Standard Wallet" warning
      ...(typeof window !== "undefined" && window.phantom?.solana?.isPhantom
        ? [] // Skip if Phantom is already available as standard wallet
        : [new PhantomWalletAdapter()]),
      // Remove Solflare to prevent MetaMask duplicate key error
      // new SolflareWalletAdapter({ network }),
    ],
    [network]
  );

  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider
        wallets={wallets}
        autoConnect={false} // Disable autoConnect to prevent hydration issues
        localStorageKey="solana-wallet-adapter"
        onError={(error) => {
          console.warn("Solana wallet adapter error:", error);
        }}
      >
        <WalletModalProvider>
          <SolanaWalletContextProvider>{children}</SolanaWalletContextProvider>
        </WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
};

export const useSolanaWallet = () => {
  const context = useContext(SolanaWalletContext);
  if (context === undefined) {
    throw new Error(
      "useSolanaWallet must be used within a SolanaWalletProvider"
    );
  }
  return context;
};
